import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubjects, Subject } from '../../api/studykit';
import { em } from '@dls/react-theme';
import {
  Button,
  LinearProgress,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@dls/react-core';
import { TableBody } from '@material-ui/core';
import { Icon } from '@dls/react-icon';
import {
  EnhancedTableHead,
  HeadCell,
  Order,
  sortedTableStyles,
} from '../../components/sorted-table/sorted-table-head';
import { getComparator, stableSort } from '../../components/sorted-table/sorting-utils';
import { RoutePaths } from '../../routes/main-routes';
import { StudyMenu } from '../../components/studies/study-menu-items';
import { BackNavigationHeader } from '../../components/styled/back-navigation-header';
import { PageWrapper, StyledButtonContainerRight } from '../../components/styled/styles';
import { ActivityContainer } from '../../components/studies/activity-container';
import { CreateSubjectDialog } from '../../components/dialogs/create-subject-dialog';
import { SubjectMenu } from '../../components/studies/subject-menu';
import useRequest from '../../api/useRequest';
import { StudyPageContext } from '../../App';
import styled from 'styled-components';
import { ColorDLS } from '../../styling/color';
import { HasPermission, usePermission } from '../../context/authorization';

const baseHeadCells: HeadCell<Subject>[] = [
  { label: 'Subject Id', type: 'sortable', numeric: false, disablePadding: true, id: 'name' },
  { label: 'Start date', type: 'sortable', numeric: false, disablePadding: true, id: 'startDate' },
  { label: 'End date', type: 'sortable', numeric: false, disablePadding: true, id: 'endDate' },
];

const activityCell: HeadCell<Subject> = {
  label: 'Activity past 14 days',
  type: 'other',
  disablePadding: true,
  id: 'status',
};

const actionsCell: HeadCell<Subject> = {
  label: '',
  type: 'other',
  disablePadding: true,
  id: '',
};

const StyledTableRow = styled(TableRow)`
  :hover {
    cursor: pointer;
    background: ${ColorDLS.Gray10};
  }
`;

const StyledToolbar = styled(Toolbar)`
  display: 'flex';
  justify-content: space-between;
`;

export const SingleStudy = () => {
  const tableClasses = sortedTableStyles();
  const navigate = useNavigate();
  const { study } = useContext(StudyPageContext);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Subject>('name');
  const [addSubjectDialogOpen, setAddSubjectDialogOpen] = useState(false);
  const canViewActivity = usePermission('SubjectsData.ViewActivity');
  const headCells = canViewActivity
    ? [...baseHeadCells, activityCell, actionsCell]
    : [...baseHeadCells, actionsCell];

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Subject) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const { data, error, mutate } = useRequest(getSubjects(study.id!));

  const handleSubjectCreated = useCallback(
    async (subject: Subject) => {
      await mutate([...(data || []), subject]);
    },
    [data, mutate],
  );

  if (error) {
    return (
      <div style={{ margin: '1.5em' }}>
        <Typography variant={'h1'}>Something went wrong..</Typography>
      </div>
    );
  }

  const onCellClick = (subject: Subject) => {
    navigate(`subjects/${subject.id}`);
  };

  return (
    <PageWrapper>
      <BackNavigationHeader
        title={study?.name || 'N/A'}
        navigateTo={`/${RoutePaths.Studies}`}
        properties={{
          'Start Date': new Date(study!.start).toLocaleDateString() || 'N/A',
          'End Date': new Date(study!.end).toLocaleDateString() || 'N/A',
          Subjects: study?.numberOfSubjects || 0,
        }}
        menu={study && <StudyMenu study={study} />}
      />
      <Paper className={'dlsTable'}>
        <CreateSubjectDialog
          open={addSubjectDialogOpen}
          onClose={() => setAddSubjectDialogOpen(false)}
          studyId={study.id}
          onSubjectCreated={handleSubjectCreated}
        />
        <StyledToolbar className={'dlsTableHeader'}>
          <Typography
            weight="bold"
            style={{
              paddingRight: `${em(24)}`,
              fontSize: `${em(18)}`,
              marginBottom: 'none',
            }}
            id="tableTitle"
          >
            Subjects
          </Typography>
          <StyledButtonContainerRight>
            <HasPermission value="Subjects.Create">
              <Button
                dls_variant="primary"
                startIcon={<Icon name={'Plus'} size={'m'} />}
                onClick={() => setAddSubjectDialogOpen(true)}
              >
                Add subject
              </Button>
            </HasPermission>
          </StyledButtonContainerRight>
        </StyledToolbar>
        {data === undefined ? (
          <LinearProgress variant={'indeterminate'} />
        ) : (
          <TableContainer style={{ width: `unset` }}>
            <Table>
              <EnhancedTableHead
                onRequestSort={handleRequestSort}
                classes={tableClasses}
                order={order}
                orderBy={orderBy}
                headCells={headCells}
              />
              <TableBody>
                {data?.length === 0 ? (
                  <TableRow data-testid={'row-no-data'}>
                    <TableCell
                      scope="row"
                      data-testid="cell-no-data"
                      colSpan={headCells.length}
                      style={{ textAlign: `center` }}
                    >
                      There are no subjects yet
                    </TableCell>
                  </TableRow>
                ) : (
                  stableSort(data, getComparator(order, orderBy)).map((row) => {
                    return (
                      <StyledTableRow key={row.name} data-testid={`row-${row.id}`}>
                        <TableCell
                          onClick={() => onCellClick(row)}
                          scope="row"
                          data-testid={`name-${row.id}`}
                        >
                          {row.name}
                        </TableCell>
                        <TableCell
                          onClick={() => onCellClick(row)}
                          scope="row"
                          data-testid={`startDate-${row.id}`}
                        >
                          {row.startDate ? new Date(row.startDate).toLocaleDateString() : 'Missing'}
                        </TableCell>
                        <TableCell
                          onClick={() => onCellClick(row)}
                          scope="row"
                          data-testid={`endDate-${row.id}`}
                        >
                          {row.endDate ? new Date(row.endDate).toLocaleDateString() : 'Missing'}
                        </TableCell>
                        {canViewActivity && (
                          <TableCell
                            onClick={() => onCellClick(row)}
                            scope="row"
                            data-testid={`activity-${row.id}`}
                          >
                            <ActivityContainer data={row.activityLast14Days!} />
                          </TableCell>
                        )}
                        <TableCell style={{ textAlign: `end` }} scope={'row'}>
                          <SubjectMenu onSubjectRemoved={mutate} subject={row} studyId={study.id} />
                        </TableCell>
                      </StyledTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </PageWrapper>
  );
};
